/**
 * useVerifyPhotos — calls cleaning-assistant in `verify_photos` mode for one
 * or more photo ids and returns Haiku's per-photo verdicts.
 *
 * Auto-fired by PhotoUploader after a successful upload so the cleaner sees
 * a verdict immediately. Failures degrade to verdict='skipped' inside the
 * Edge Function — this hook never throws on per-photo issues.
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                          from './useSupabase'
import { AppError }                             from '@/lib/errors/AppError'

export interface PhotoVerdict {
  photo_id: string
  category: string
  /**
   * Mirrors the server-side enum in supabase/functions/cleaning-assistant.
   * `unavailable` indicates an Anthropic upstream outage (5xx / 429 / 408)
   * — per CLAUDE.md ERROR 16 the cleaner is told to escalate, never to
   * "looks fine, ship it." Must stay in sync with the server type.
   */
  verdict:  'ok' | 'send_back' | 'unclear' | 'skipped' | 'unavailable'
  reason:   string
}

interface VerifyVars {
  taskId:   string
  photoIds: readonly string[]
}

export function useVerifyPhotos(): UseMutationResult<PhotoVerdict[], AppError, VerifyVars> {
  const supabase = useSupabase()

  return useMutation<PhotoVerdict[], AppError, VerifyVars>({
    mutationFn: async ({ taskId, photoIds }): Promise<PhotoVerdict[]> => {
      if (photoIds.length === 0) return []
      const { data, error } = await supabase.functions.invoke<{ verdicts: PhotoVerdict[] }>('cleaning-assistant', {
        body: {
          cleaning_task_id: taskId,
          mode:             'verify_photos',
          photo_ids:        Array.from(photoIds),
        },
      })
      if (error) {
        throw new AppError({
          code:        'VERIFY_FAILED',
          message:     error.message,
          userMessage: 'Could not run vision check on the new photo.',
          status:      500,
          cause:       error,
        })
      }
      return data?.verdicts ?? []
    },
  })
}
